import React, { useState } from 'react';
import { useNotif } from '../context/NotifContext';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined,
    NotificationsOutlined, TrendingUpOutlined,
    SouthWestOutlined, NorthEastOutlined,
    FlashOnOutlined, CreditCardOutlined,
    LocalAtmOutlined, ShareOutlined,
    CheckCircleOutline, WarningAmberOutlined,
    InfoOutlined, DeleteOutlineOutlined,
    DoneAllOutlined, CircleOutlined, Circle,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    orange:  '#FF9500',
    blue:    '#007AFF',
    gold:    '#F5C842',
};

interface Notif {
    id: number;
    type: 'credit' | 'debit' | 'alert' | 'promo' | 'score' | 'info';
    title: string;
    body: string;
    time: string;
    read: boolean;
    group: 'Today' | 'Yesterday' | 'Earlier';
}

const INITIAL_NOTIFS: Notif[] = [
    { id: 1,  type: 'credit', title: 'Salary Received',         body: '₦80,000 credited to your wallet from Access Bank.',                     time: '9:14 AM',    read: false, group: 'Today'     },
    { id: 2,  type: 'alert',  title: 'Login from new device',   body: 'A new sign-in was detected from Chrome on Windows. Not you? Secure your account.', time: '7:40 AM',    read: false, group: 'Today'     },
    { id: 3,  type: 'score',  title: 'Credit score updated',    body: 'Your credit score improved by 12 points to 653. Keep it up!',            time: '6:00 AM',    read: false, group: 'Today'     },
    { id: 4,  type: 'debit',  title: 'Electricity Bill Paid',   body: '₦15,000 paid to EKEDC. Token: 4821-3302-1192-0042.',                    time: 'Yesterday',  read: true,  group: 'Yesterday' },
    { id: 5,  type: 'credit', title: 'Wallet Funded',           body: '₦20,000 added via GTBank transfer.',                                    time: 'Yesterday',  read: true,  group: 'Yesterday' },
    { id: 6,  type: 'promo',  title: 'Refer & earn ₦1,000',    body: 'Invite a friend to NG Credit and earn ₦1,000 when they sign up.',        time: 'Yesterday',  read: true,  group: 'Yesterday' },
    { id: 7,  type: 'debit',  title: 'Transfer Sent',           body: '₦5,000 sent to Emeka Okafor · Zenith Bank.',                            time: 'Mon',        read: true,  group: 'Earlier'   },
    { id: 8,  type: 'alert',  title: 'Low balance warning',     body: 'Your wallet balance dropped below ₦5,000. Top up to keep transacting.',  time: 'Mon',        read: true,  group: 'Earlier'   },
    { id: 9,  type: 'info',   title: 'Loan repayment reminder', body: 'Your Quick Cash loan repayment of ₦17,500 is due in 3 days.',            time: 'Sun',        read: true,  group: 'Earlier'   },
    { id: 10, type: 'score',  title: 'New credit tip',          body: 'Keeping your credit utilisation below 30% can improve your score by up to 40 points.', time: 'Sat', read: true, group: 'Earlier' },
];

const NOTIF_META: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
    credit: { icon: SouthWestOutlined,  color: B.green,  bg: B.greenDim   },
    debit:  { icon: NorthEastOutlined,  color: B.red,    bg: B.redDim     },
    alert:  { icon: WarningAmberOutlined, color: B.orange, bg: `${B.orange}18` },
    promo:  { icon: ShareOutlined,      color: '#FF2D55', bg: `#FF2D5518`  },
    score:  { icon: TrendingUpOutlined, color: B.purple, bg: `${B.purple}18` },
    info:   { icon: InfoOutlined,       color: B.blue,   bg: `${B.blue}18`  },
};

const FILTER_TABS = ['All', 'Unread', 'Transactions', 'Alerts'];

const NotificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [notifs,    setNotifs]    = useState<Notif[]>(INITIAL_NOTIFS);
    const [activeTab, setActiveTab] = useState('All');
    const { clearUnread } = useNotif();

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const unreadCount = notifs.filter(n => !n.read).length;

    const markAllRead = () => setNotifs(ns => ns.map(n => ({ ...n, read: true })));
    const markRead    = (id: number) => setNotifs(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
    const dismiss     = (id: number) => setNotifs(ns => ns.filter(n => n.id !== id));

    const filtered = notifs.filter(n => {
        if (activeTab === 'Unread')       return !n.read;
        if (activeTab === 'Transactions') return n.type === 'credit' || n.type === 'debit';
        if (activeTab === 'Alerts')       return n.type === 'alert' || n.type === 'info';
        return true;
    });

    const groups = ['Today', 'Yesterday', 'Earlier'] as const;

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.purple, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate('/wallet')}
                        sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Notifications
                        </Typography>
                    </Box>
                    {unreadCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.red, .2), border: `1px solid ${alpha(B.red, .35)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: B.red, boxShadow: `0 0 6px ${B.red}` }} />
                            <Typography sx={{ color: B.red, fontSize: '.68rem', fontWeight: 700 }}>{unreadCount} NEW</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Filter tabs + mark all read ── */}
                <Box sx={{ ...glassCard, p: 2, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.75}>
                        <Box sx={{ display: 'flex', gap: .75, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                            {FILTER_TABS.map(tab => (
                                <Box key={tab} onClick={() => setActiveTab(tab)} sx={{
                                    flexShrink: 0, px: 1.5, py: .55, borderRadius: '20px', cursor: 'pointer', fontSize: '.72rem', fontWeight: 600,
                                    background: activeTab === tab ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : alpha(isDark ? '#fff' : B.dark, .05),
                                    color: activeTab === tab ? '#fff' : textFaint,
                                    border: `1px solid ${activeTab === tab ? 'transparent' : dividerC}`,
                                    transition: 'all .15s',
                                    boxShadow: activeTab === tab ? `0 4px 12px ${alpha(B.purple, .3)}` : 'none',
                                }}>
                                    {tab}
                                </Box>
                            ))}
                        </Box>
                        {unreadCount > 0 && (
                            <Button size="small" onClick={() => { markAllRead(); clearUnread(); }} startIcon={<DoneAllOutlined sx={{ fontSize: 13 }} />}
                                sx={{ color: B.purple, fontWeight: 600, fontSize: '.68rem', textTransform: 'none', px: 1, flexShrink: 0, '&:hover': { background: alpha(B.purple, .07) } }}>
                                Mark all read
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* ── Notification groups ── */}
                {filtered.length === 0 ? (
                    <Box sx={{ ...glassCard, py: 6, textAlign: 'center' }}>
                        <NotificationsOutlined sx={{ color: textFaint, fontSize: 40, mb: 1.5 }} />
                        <Typography sx={{ color: textMid, fontWeight: 600, fontSize: '.9rem' }}>All caught up</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.78rem', mt: .5 }}>No {activeTab.toLowerCase()} notifications</Typography>
                    </Box>
                ) : (
                    groups.map(group => {
                        const items = filtered.filter(n => n.group === group);
                        if (!items.length) return null;
                        return (
                            <Box key={group} sx={{ mb: 2 }}>
                                {/* Group label */}
                                <Typography sx={{ color: textFaint, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', mb: 1, px: .5 }}>
                                    {group}
                                </Typography>

                                <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                                    {items.map((notif, i) => {
                                        const meta = NOTIF_META[notif.type];
                                        const Icon = meta.icon;
                                        return (
                                            <React.Fragment key={notif.id}>
                                                <Box
                                                    onClick={() => markRead(notif.id)}
                                                    sx={{
                                                        px: 2, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.75,
                                                        cursor: 'pointer', transition: 'background .15s',
                                                        background: !notif.read ? alpha(B.purple, isDark ? .08 : .04) : 'transparent',
                                                        '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) },
                                                        position: 'relative',
                                                    }}>

                                                    {/* Unread dot */}
                                                    {!notif.read && (
                                                        <Box sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 5, height: 5, borderRadius: '50%', background: B.purple, boxShadow: `0 0 6px ${B.purple}` }} />
                                                    )}

                                                    {/* Icon */}
                                                    <Box sx={{ width: 42, height: 42, borderRadius: '13px', background: meta.bg, border: `1px solid ${alpha(meta.color, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: !notif.read ? .75 : 0 }}>
                                                        <Icon sx={{ color: meta.color, fontSize: 19 }} />
                                                    </Box>

                                                    {/* Content */}
                                                    <Box flex={1} minWidth={0}>
                                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                            <Typography sx={{ color: notif.read ? textMid : textPri, fontWeight: notif.read ? 500 : 700, fontSize: '.84rem', letterSpacing: '-.01em', lineHeight: 1.3 }}>
                                                                {notif.title}
                                                            </Typography>
                                                            <Typography sx={{ color: textFaint, fontSize: '.62rem', flexShrink: 0, mt: .25 }}>{notif.time}</Typography>
                                                        </Box>
                                                        <Typography sx={{ color: textFaint, fontSize: '.75rem', mt: .4, lineHeight: 1.5, pr: 1 }}>
                                                            {notif.body}
                                                        </Typography>
                                                    </Box>

                                                    {/* Dismiss */}
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => { e.stopPropagation(); dismiss(notif.id); }}
                                                        sx={{ p: .3, color: textFaint, flexShrink: 0, mt: .25, opacity: 0, transition: 'opacity .15s',
                                                            '.MuiBox-root:hover > &': { opacity: 1 },
                                                            '&:hover': { color: B.red },
                                                        }}>
                                                        <DeleteOutlineOutlined sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                                {i < items.length - 1 && <Divider sx={{ ml: '74px', mr: 2, borderColor: dividerC }} />}
                                            </React.Fragment>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })
                )}

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default NotificationsPage;